import { AppEvent } from '$lib/helper/appEvents';
import { isSoundAllowed, playSound } from '$lib/helper/sound';
import type {
	BookingReviewUnreadCountUpdatedMessage,
	GiftcardConsumedMessage,
	GiftcardCreatedMessage,
	GiftcardDeletedMessage,
	Message,
	NotificationCreatedMessage,
	NotificationReadMessage,
	NotificationUnreadMessage,
	UserDoNotDisturbUpdatedMessage
} from '$lib/model/socketMessage';
import { unreadReviewsCountStore } from '$lib/stores/review';
import { loadSmsRefills } from '$lib/stores/smsRefills';
import { loadSmsSettings } from '$lib/stores/smsSettings';
import { DateTime } from 'luxon';
import { convertApiScanToScan, scansHistory, type ApiScan } from '../qr-code/qr-code';
import { loadContract } from './billing';
import { loadBooking } from './booking';
import { doNotDisturbStore } from './doNotDisturb';
import { apiToAppGiftcardTransformer, giftcardStore } from './giftcard';
import { markListAsNotFreshUnlessExists, refreshItemInList } from './infinity-list';
import {
	notificationStatsStore,
	notificationStore,
	transformApiNotificationToNotification
} from './notification';
import { notificationSettingStore } from './notification-setting';
import { openingHourStore } from './opening-hour';
import { loadSessionInstance } from './session-instance';
import { socketEventWatcherStore } from './socket';

let loaded = false;

export function initSocketWatchers() {
	if (loaded) return;
	loaded = true;

	socketEventWatcherStore.register('openingHour', (message: Message) => {
		if (message.event === 'opening_hour_updated' || message.event === 'opening_hour_created') {
			openingHourStore.refresh();
		}
	});

	socketEventWatcherStore.register('contractUpdate', (message: Message) => {
		if (
			message.event === 'billing_plan_cash_collect' ||
			message.event === 'billing_plan_sanction' ||
			message.event === 'billing_plan_sanction_reactivation' ||
			message.event === 'billing_plan_sanction_warning' ||
			message.event === 'billing_plan_sanction_suspension'
		) {
			loadContract(true);
		}
	});

	// Make the doNotDisturb store react to sockets events by adding a watcher
	socketEventWatcherStore.register('doNotDisturbWatcher', (message: Message) => {
		if (message.event === AppEvent.USER_DO_NOT_DISTURB_UPDATED) {
			const until = (message as UserDoNotDisturbUpdatedMessage).data.until;

			if (until) {
				doNotDisturbStore.set({
					enabled: true,
					end: DateTime.fromISO(until)
				});
			} else {
				doNotDisturbStore.set({
					enabled: false,
					end: null
				});
			}
		}
	});

	// Make the giftcard store react to sockets events by adding a watcher
	socketEventWatcherStore.register('giftcardWatcher', (message: Message) => {
		if (message.event === AppEvent.GIFTCARD_CONSUMED) {
			refreshItemInList(
				(message as GiftcardConsumedMessage).data.giftcard_id,
				'id',
				giftcardStore,
				{
					apiToAppItemTransformer: apiToAppGiftcardTransformer
				}
			);
		}
		if (message.event === AppEvent.GIFTCARD_DELETED) {
			refreshItemInList(
				(message as GiftcardDeletedMessage).data.giftcard_id,
				'id',
				giftcardStore
			);
		}
		if (message.event === AppEvent.GIFTCARD_CREATED) {
			markListAsNotFreshUnlessExists(
				(message as GiftcardCreatedMessage).data.giftcard_id,
				'id',
				giftcardStore
			);
		}
	});

	const knownNotificationsIds: number[] = [];
	// Make the notification store react to sockets events by adding a watcher
	socketEventWatcherStore.register('notificationWatcher', (message: Message) => {
		if (message.event === AppEvent.NOTIFICATION_CREATED) {
			const notificationId = (message as NotificationCreatedMessage).data.notification_id;
			const notification = transformApiNotificationToNotification(
				(message as NotificationCreatedMessage).data.notification
			);

			// Do nothing if the notification is already known by our store (duplicate socket message)
			if (knownNotificationsIds.includes(notificationId)) {
				return;
			}

			knownNotificationsIds.push(notificationId);
			markListAsNotFreshUnlessExists(notificationId, 'id', notificationStore);

			if (isSoundAllowed()) {
				const sound = notificationSettingStore.getSound(notification.event);
				playSound(sound);
			}

			notificationStatsStore.update((value) => {
				return {
					unread: value.unread + 1,
					knownNotificationsIds: [...value.knownNotificationsIds, notificationId]
				};
			});
		}
		if (message.event === AppEvent.NOTIFICATION_ALL_READ) {
			notificationStore.update((store) => {
				store.data.forEach((notification) => {
					notification.isRead = true;
				});
				return store;
			});

			notificationStatsStore.update((value) => {
				return { unread: 0, knownNotificationsIds: value.knownNotificationsIds };
			});
		}
		if (message.event === AppEvent.NOTIFICATION_READ) {
			refreshItemInList(
				(message as NotificationReadMessage).data.notification_id,
				'id',
				notificationStore,
				{ apiToAppItemTransformer: transformApiNotificationToNotification }
			);
			notificationStatsStore.refresh();
		}
		if (message.event === AppEvent.NOTIFICATION_UNREAD) {
			refreshItemInList(
				(message as NotificationUnreadMessage).data.notification_id,
				'id',
				notificationStore,
				{ apiToAppItemTransformer: transformApiNotificationToNotification }
			);
			notificationStatsStore.refresh();
		}
		if (message.event === AppEvent.BOOKING_REVIEW_UNREAD_COUNT_UPDATED) {
			const count = (message as BookingReviewUnreadCountUpdatedMessage).data.count;
			unreadReviewsCountStore.update(() => count);
		}
	});

	// Make the booking store react to sockets events by adding a watcher
	socketEventWatcherStore.register('bookingWatch', (message: Message) => {
		if ('booking_id' in message.data) {
			loadBooking(message.data.booking_id, (b) => {
				loadSessionInstance(b.date);
			});
		}
	});

	// Make the booking store react to sockets events by adding a watcher
	socketEventWatcherStore.register('qr_code_scanned', (message: Message) => {
		if (message.event === AppEvent.QR_CODE_SCANNED) {
			const s = convertApiScanToScan(message.data as unknown as ApiScan);
			scansHistory.update((history) => [
				...history.filter((scan) => scan.code !== s.code),
				s
			]);
		}
	});

	// Make sms refill store react to sockets events by adding a watcher
	socketEventWatcherStore.register('smsRefillsWatcher', (message: Message) => {
		if (
			message.event === AppEvent.SMS_CREDITS_REFILL_REQUEST_AUTO ||
			message.event === AppEvent.SMS_CREDITS_REFILL_REQUEST_MANUAL
		) {
			loadSmsRefills();
		}
		if (message.event === AppEvent.SMS_CREDITS_UPDATED) {
			loadSmsSettings();
			loadSmsRefills();
		}
	});
}
